import React from 'react';
import { createPortal } from 'react-dom';
import type { ScanCountLine, ScanCountProblem, ScanCountProblemKind } from '@/domain/warehouse';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import { CloseIcon } from '../../ui/CloseIcon';
import { readableErrorMessage } from '../../utils/appErrors';
import { holdFocusOnBackdrop, trapTabKey } from './dialogDom';

const KINDS: { value: ScanCountProblemKind; label: string }[] = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'missing', label: 'Missing' },
  { value: 'extra', label: 'Extra' },
  { value: 'other', label: 'Other' },
];

type ReportProblemDialogProps = {
  lines: readonly ScanCountLine[];
  onClose: () => void;
  /** Saves the report; a rejection keeps the dialog open with the reason beside Save. */
  onSave: (problem: ScanCountProblem) => Promise<void>;
};

/**
 * "Report a problem" from the Receive/Pick dialog: what happened, an optional line and a note.
 * A nested dialog on the 9100 layer (docs/modal-layering.md), portalled to the body so the parent
 * dialog's transform cannot contain it. Escape and the X close it; the parent keeps its count.
 */
export default function ReportProblemDialog({ lines, onClose, onSave }: ReportProblemDialogProps) {
  const titleId = React.useId();
  const kindName = React.useId();
  const lineFieldId = React.useId();
  const noteId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const firstKindRef = React.useRef<HTMLInputElement>(null);
  const mountedRef = React.useRef(true);
  const [kind, setKind] = React.useState<ScanCountProblemKind | ''>('');
  const [lineId, setLineId] = React.useState('');
  const [note, setNote] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  useModalScrollLock(true);

  React.useEffect(() => {
    mountedRef.current = true;
    firstKindRef.current?.focus({ preventScroll: true });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      // A native select handles its own Escape.
      if (event.defaultPrevented || document.activeElement instanceof HTMLSelectElement) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key === 'Tab' && dialogRef.current) {
      trapTabKey(event, dialogRef.current);
      event.stopPropagation();
    }
  }

  function handleLineChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setLineId(event.target.value);
  }

  function handleNoteChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setNote(event.target.value);
    if (error) setError(null);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const text = note.trim();
    if (!kind || !text) {
      setError(!kind && !text ? 'Choose what happened and add a note.' : !kind ? 'Choose what happened.' : 'Add a note about the problem.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ kind, note: text, lineId: lineId || undefined });
    } catch (saveError) {
      if (mountedRef.current) setError(readableErrorMessage(saveError, 'The problem was not saved. Try again.', 'mutation'));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="modal-overlay scan-count-problem-overlay" onMouseDown={holdFocusOnBackdrop}>
      <div
        ref={dialogRef}
        className="modal scan-count-problem"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <form className="scan-count-problem__form" onSubmit={handleSubmit} noValidate>
          <header className="modal__header scan-count-problem__header">
            <div className="scan-count-dialog__heading">
              <h2 id={titleId} className="modal__title scan-count-dialog__title">
                Report a problem
              </h2>
              <p className="scan-count-dialog__subtitle">It is noted on the record.</p>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </button>
          </header>
          <div className="modal__body">
            <div className="modal__body-content scan-count-problem__body">
              <fieldset className="scan-count-problem__kinds">
                <legend>What happened?</legend>
                <div className="scan-count-problem__kind-options">
                  {KINDS.map((option, index) => (
                    <label key={option.value} className="scan-count-problem__kind">
                      <input
                        ref={index === 0 ? firstKindRef : undefined}
                        type="radio"
                        name={kindName}
                        value={option.value}
                        checked={kind === option.value}
                        onChange={() => setKind(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="scan-count-problem__field">
                <label htmlFor={lineFieldId}>Line (optional)</label>
                <select id={lineFieldId} value={lineId} onChange={handleLineChange}>
                  <option value="">Not about one line</option>
                  {lines.map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.sku ? `${line.name} (${line.sku})` : line.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="scan-count-problem__field">
                <label htmlFor={noteId}>Note</label>
                <textarea
                  id={noteId}
                  rows={4}
                  value={note}
                  onChange={handleNoteChange}
                  placeholder="For example: two fixtures arrived with cracked lenses."
                />
              </div>
            </div>
          </div>
          <footer className="modal__footer scan-count-problem__footer">
            {error ? (
              <p className="scan-count-problem__error" role="alert">
                {error}
              </p>
            ) : null}
            {/* aria-disabled, not disabled, while saving: focus stays on Save for the outcome. */}
            <button type="submit" className="btn btn-primary" aria-disabled={saving || undefined}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
